import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { UserRoleService } from '../services/user-role.service';
import { ContactService } from '../core/services/contact.service';
import { I18nStore } from '../core/features/translations';
import { PortalUiService } from '../services/portal-ui.service';
import { FooterComponent } from '../shared/footer/footer';
import { switchMap, firstValueFrom } from 'rxjs';

/**
 * OAuth2 Callback Component
 *
 * This component handles the OAuth2 authorization callback.
 * It receives the authorization code from the OAuth provider,
 * exchanges it for tokens, fetches the user's portal role, and redirects to the dashboard.
 *
 * Route: /auth/callback
 * Query params: code, state, error
 */
@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.html',
  imports: [FooterComponent],
  standalone: true,
})
export class AuthCallback implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly userRoleService = inject(UserRoleService);
  private readonly contactService = inject(ContactService);
  private readonly i18nStore = inject(I18nStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly portalUi = inject(PortalUiService);

  errorMessage = signal<string | null>(null);
  isProcessing = signal(true);

  async ngOnInit(): Promise<void> {
    // Get query parameters
    const params = this.route.snapshot.queryParams;
    const code = params['code'] as string | undefined;
    const state = params['state'] as string | undefined;
    const error = params['error'] as string | undefined;
    const errorDescription = params['error_description'] as string | undefined;

    // Handle OAuth error
    if (error) {
      console.error('OAuth error received:', error, errorDescription);
      this.errorMessage.set(errorDescription || error);
      this.isProcessing.set(false);

      // Redirect to login with error message after 3 seconds
      setTimeout(() => {
        this.router.navigate(['/login'], {
          queryParams: { error: errorDescription || error },
        });
      }, 3000);
      return;
    }

    // Validate code parameter
    if (!code) {
      console.error('No authorization code received');
      this.errorMessage.set('No authorization code received. Please try again.');
      this.isProcessing.set(false);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 2000);
      return;
    }

    // Exchange code for tokens
    try {
      await this.authService.exchangeCodeForToken(code, state);

      // Start token expiration monitoring
      this.tokenRefreshService.startMonitoring();

      // Get user info from auth service
      const userInfo = this.authService.getUserInfo();
      if (!userInfo) {
        throw new Error('No user info found after token exchange');
      }

      // Get user's email from OIDC token claims
      const userEmail = userInfo.sub || userInfo.email;
      if (!userEmail) {
        throw new Error('No email found in user info');
      }

      // Get contact by email and then fetch the portal role
      try {
        const roleData = await firstValueFrom(
          this.contactService.getContactByEmail(userEmail).pipe(
            switchMap((contact) => {
              return this.userRoleService.fetchPortalRole(contact.contact_id);
            }),
          ),
        );

        // Check if user has a valid role
        if (!roleData.is_student && !roleData.is_lecturer) {
          console.warn('⚠️ User has no valid portal role (neither student nor lecturer)');
          this.errorMessage.set(
            'Sie haben keine gültige Rolle im Portal (weder Student noch Dozent).',
          );
          this.isProcessing.set(false);

          // Redirect to login after 5 seconds
          setTimeout(() => {
            this.authService.logout();
            this.router.navigate(['/login'], {
              queryParams: { error: 'no_valid_role' },
            });
          }, 5000);
          return;
        }

        // Apply associated schools from roleData to header banner
        if (roleData.schools && roleData.schools.length > 0) {
          const schoolShortnames = roleData.schools.map((s) => s.school_shortname);
          this.portalUi.setActiveSchools(schoolShortnames);
        }
      } catch (roleError) {
        // If role fetch fails, log error but continue to dashboard
        console.error('⚠️ Could not fetch user role (continuing anyway):', roleError);
      }

      // ✅ Initialize I18n after successful login
      // This ensures translations and language preference are loaded before navigating to dashboard
      try {
        await this.i18nStore.refreshTranslations();
      } catch (i18nError) {
        console.error('⚠️ I18n initialization failed (continuing anyway):', i18nError);
      }

      // Clean redirect to dashboard (no query params)
      await this.router.navigate(['/app/dashboard'], { replaceUrl: true });
    } catch (error: unknown) {
      console.error('Token exchange failed:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Authentication failed. Please try again.';
      this.errorMessage.set(errorMessage);
      this.isProcessing.set(false);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 3000);
    }
  }
}
